import React, { useState, useEffect, useContext, useMemo } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend,
} from "chart.js";
import { GlobalContext } from "../../Utils/GlobalContext";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const TIPOS_WIDGET = [
  { value: "donut",          label: "Rosca (Donut)",    descricao: "Proporção entre categorias" },
  { value: "bar_vertical",   label: "Barra Vertical",   descricao: "Contagem por categoria" },
  { value: "bar_horizontal", label: "Barra Horizontal", descricao: "Comparação entre grupos (com %)" },
  { value: "bar_stacked",    label: "Barra Empilhada",  descricao: "Distribuição proporcional por grupo" },
  { value: "bar_timeline",   label: "Linha do Tempo",   descricao: "Evolução mensal com agrupamento por ano" },
  { value: "kpi",            label: "KPI — Total",      descricao: "Número único de contagem" },
];

// Campos disponíveis: correspondem exatamente aos aliases da sp_relatorio_documentos
export const CAMPOS_GRAFICO = [
  { value: "situacao",            label: "Situação" },
  { value: "vencido",             label: "Vencido?" },
  { value: "tipo documento",      label: "Tipo de Documento" },
  { value: "tipo pagamento",      label: "Tipo de Pagamento" },
  { value: "fornecedor",          label: "Fornecedor" },
  { value: "empresa",             label: "Empresa" },
  { value: "funcionario",         label: "Funcionário" },
  { value: "usuario responsavel", label: "Responsável" },
  { value: "cidade",              label: "Cidade" },
  { value: "indice correcao",     label: "Índice de Correção" },
];

function gerarId() {
  return "w" + Math.random().toString(36).slice(2, 8);
}

// Ícone de pizza colorida (SVG inline) — igual para todos os cards
function IconePizza({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" style={{ display: "block" }}>
      {/* ~40% azul */}
      <path d="M10 10 L10 2 A8 8 0 0 1 14.7 16.5 Z" fill="#0d6efd" />
      {/* ~35% verde */}
      <path d="M10 10 L14.7 16.5 A8 8 0 0 1 2 10 Z" fill="#28a745" />
      {/* ~25% laranja */}
      <path d="M10 10 L2 10 A8 8 0 0 1 10 2 Z" fill="#fd7e14" />
    </svg>
  );
}

// ─── Dados de exemplo para os previews ───────────────────────────────────────
const CORES_PREVIEW = ["#28a745", "#6c757d", "#ffc107", "#dc3545", "#0dcaf0", "#6f42c1"];

const PREVIEW_SAMPLES = {
  donut: {
    labels: ["Vigente", "Arquivado", "Pendente", "Encerrado"],
    datasets: [{ data: [45, 28, 15, 12], backgroundColor: ["#28a745","#6c757d","#ffc107","#dc3545"], borderWidth: 1, borderColor: "transparent" }],
  },
  bar_vertical: {
    labels: ["Contrato", "NF Serviço", "Alvará", "Certidão", "Outros"],
    datasets: [{ data: [22, 15, 8, 5, 3], backgroundColor: ["#0d6efd","#28a745","#ffc107","#dc3545","#6f42c1"], borderRadius: 3, borderWidth: 0 }],
  },
  bar_horizontal: {
    labels: ["Empresa Sul", "Empresa Norte", "Empresa Leste", "Empresa Oeste"],
    datasets: [
      { label: "Vigente",   data: [18, 12, 9, 6], backgroundColor: "#28a745" },
      { label: "Arquivado", data: [8,  5,  3, 2],  backgroundColor: "#6c757d" },
    ],
  },
  bar_stacked: {
    grupos: ["Contrato", "NF Serviço", "Alvará", "Certidão"],
    splits: ["Vigente", "Arquivado", "Pendente"],
    matrix: {
      "Contrato":   { "Vigente": 18, "Arquivado": 8, "Pendente": 4 },
      "NF Serviço": { "Vigente": 12, "Arquivado": 5, "Pendente": 2 },
      "Alvará":     { "Vigente": 7,  "Arquivado": 3, "Pendente": 1 },
      "Certidão":   { "Vigente": 5,  "Arquivado": 2, "Pendente": 0 },
    },
  },
  bar_timeline: [
    { label: "01/2026", total: 3 },
    { label: "02/2026", total: 5 },
    { label: "03/2026", total: 8 },
    { label: "04/2026", total: 6 },
    { label: "05/2026", total: 11 },
    { label: "06/2026", total: 9 },
    { label: "07/2026", total: 4 },
  ],
  kpi: 127,
};

// ─── Renderiza o conteúdo do preview para cada tipo ──────────────────────────
function PreviewConteudo({ tipo, darkMode }) {
  const texto = darkMode ? "#e2e8f0" : "#212529";
  const borda = darkMode ? "#30364a" : "#dee2e6";

  const opcBase = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 250 },
    plugins: {
      legend: {
        position: "bottom",
        labels: { font: { size: 10 }, padding: 8, boxWidth: 10, color: darkMode ? "#94a3b8" : "#495057" },
      },
    },
  };

  if (tipo === "donut") {
    return (
      <div style={{ height: 230 }}>
        <Doughnut
          data={PREVIEW_SAMPLES.donut}
          options={{ ...opcBase, cutout: "62%", plugins: { ...opcBase.plugins, tooltip: { callbacks: { label: (ctx) => { const t = ctx.dataset.data.reduce((a, b) => a + b, 0); return ` ${ctx.label}: ${ctx.parsed} (${((ctx.parsed/t)*100).toFixed(0)}%)`; } } } } }}
        />
      </div>
    );
  }

  if (tipo === "bar_vertical") {
    return (
      <div style={{ height: 210 }}>
        <Bar
          data={PREVIEW_SAMPLES.bar_vertical}
          options={{
            ...opcBase,
            indexAxis: "x",
            plugins: { ...opcBase.plugins, legend: { display: false } },
            scales: {
              y: { beginAtZero: true, ticks: { font: { size: 10 }, color: "#6c757d", stepSize: 5 }, grid: { color: darkMode ? "#30364a55" : "#f0f0f0" } },
              x: { ticks: { font: { size: 10 }, color: "#6c757d" }, grid: { display: false } },
            },
          }}
        />
      </div>
    );
  }

  if (tipo === "bar_horizontal") {
    return (
      <div style={{ height: 210 }}>
        <Bar
          data={PREVIEW_SAMPLES.bar_horizontal}
          options={{
            ...opcBase,
            indexAxis: "y",
            scales: {
              x: { beginAtZero: true, ticks: { font: { size: 10 }, color: "#6c757d" }, grid: { color: darkMode ? "#30364a55" : "#f0f0f0" } },
              y: { ticks: { font: { size: 10 }, color: "#6c757d" }, grid: { display: false } },
            },
          }}
        />
      </div>
    );
  }

  if (tipo === "bar_stacked") {
    const { grupos, splits, matrix } = PREVIEW_SAMPLES.bar_stacked;
    return (
      <div style={{ padding: "4px 0" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {grupos.map((grupo) => {
            const total = splits.reduce((s, sp) => s + (matrix[grupo]?.[sp] ?? 0), 0);
            return (
              <div key={grupo} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 76, flexShrink: 0, fontSize: "0.72rem", fontWeight: 700, color: texto, textAlign: "right", whiteSpace: "nowrap" }}>
                  {grupo}
                </div>
                <div style={{ flex: 1, display: "flex", height: 26, borderRadius: 5, overflow: "hidden" }}>
                  {splits.map((sp, si) => {
                    const val = matrix[grupo]?.[sp] ?? 0;
                    const pct = (val / total) * 100;
                    if (pct < 0.1) return null;
                    return (
                      <div
                        key={sp}
                        style={{ width: `${pct}%`, background: CORES_PREVIEW[si], display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "width 0.2s" }}
                        title={`${sp}: ${val} (${pct.toFixed(0)}%)`}
                      >
                        {pct >= 12 && (
                          <span style={{ fontSize: "0.65rem", fontWeight: 700, color: "#fff", userSelect: "none" }}>
                            {pct.toFixed(0)}%
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: "4px 14px", flexWrap: "wrap", marginTop: 14 }}>
          {splits.map((sp, si) => (
            <div key={sp} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.72rem", color: texto }}>
              <div style={{ width: 9, height: 9, borderRadius: 2, background: CORES_PREVIEW[si], flexShrink: 0 }} />
              {sp}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tipo === "bar_timeline") {
    const pontos = PREVIEW_SAMPLES.bar_timeline;
    const maxTotal = Math.max(...pontos.map((p) => p.total), 1);
    return (
      <div style={{ height: 190, display: "flex", flexDirection: "column", padding: "6px 0 0" }}>
        <div style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 5, minHeight: 0 }}>
          {pontos.map((p) => {
            const hPct = (p.total / maxTotal) * 100;
            return (
              <div key={p.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "flex-end" }}>
                <span style={{ fontSize: "0.62rem", fontWeight: 700, color: texto, marginBottom: 3, lineHeight: 1 }}>{p.total}</span>
                <div
                  style={{ width: "68%", minWidth: 6, height: `${hPct}%`, minHeight: 4, background: "#6ea8fe", borderRadius: "4px 4px 0 0", transition: "height 0.2s" }}
                  title={`${p.label}: ${p.total}`}
                />
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 5, paddingTop: 5, borderTop: `1px solid ${borda}`, marginTop: 4 }}>
          {pontos.map((p) => (
            <div key={p.label} style={{ flex: 1, textAlign: "center", fontSize: "0.56rem", color: "#6c757d", lineHeight: 1.2 }}>
              {p.label}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tipo === "kpi") {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 170, gap: 10 }}>
        <div style={{ fontSize: "4.5rem", fontWeight: 800, color: "#0d6efd", lineHeight: 1, letterSpacing: "-2px" }}>
          {PREVIEW_SAMPLES.kpi}
        </div>
        <div style={{ fontSize: "0.82rem", color: "#6c757d", fontWeight: 500 }}>total de documentos</div>
      </div>
    );
  }

  return null;
}

// ─── Modal de preview ─────────────────────────────────────────────────────────
function PreviewModal({ tipo, onClose, darkMode }) {
  const info  = TIPOS_WIDGET.find((t) => t.value === tipo);
  const bg    = darkMode ? "#1c2235" : "#fff";
  const borda = darkMode ? "#30364a" : "#dee2e6";
  const texto = darkMode ? "#e2e8f0" : "#212529";

  return (
    <Modal show={!!tipo} onHide={onClose} centered size="sm" enforceFocus={false}>
      <Modal.Header closeButton style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda }}>
        <Modal.Title style={{ color: texto, fontSize: "0.9rem" }}>
          {info?.label}
          <div style={{ fontSize: "0.72rem", fontWeight: 400, color: "#6c757d", marginTop: 1 }}>{info?.descricao}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: bg, padding: "14px 18px" }}>
        {tipo && <PreviewConteudo tipo={tipo} darkMode={darkMode} />}
        <div style={{ marginTop: 12, padding: "8px 10px", background: darkMode ? "#161b27" : "#f8f9fa", borderRadius: 6, fontSize: "0.72rem", color: "#6c757d", borderLeft: "3px solid #0d6efd" }}>
          Dados ilustrativos — o gráfico real usará os dados do dashboard.
        </div>
      </Modal.Body>
      <Modal.Footer style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda }} className="modal-footer-padrao">
        <div className="modal-footer-acoes-esquerda" />
        <div className="modal-footer-acoes-direita">
          <Button size="sm" variant="secondary" onClick={onClose}>Fechar</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Modal principal ──────────────────────────────────────────────────────────
export default function DashboardWidgetConfigModal({ show, onClose, widget, onSalvar, camposData = [], camposAdicionais = [] }) {
  const { darkMode } = useContext(GlobalContext);

  const [titulo,      setTitulo]      = useState("");
  const [tipo,        setTipo]        = useState("donut");
  const [campoGrupo,  setCampoGrupo]  = useState(CAMPOS_GRAFICO[0].value);
  const [campoSplit,  setCampoSplit]  = useState("");
  const [previewTipo, setPreviewTipo] = useState(null);

  // Campos não-data disponíveis para agrupar: padrão + customizados não-data
  const todosCamposNaoData = useMemo(
    () => [...CAMPOS_GRAFICO, ...camposAdicionais],
    [camposAdicionais]
  );

  useEffect(() => {
    if (!show) return;
    const tipoInicial = widget?.tipo ?? "donut";
    setTitulo(widget?.titulo ?? "");
    setTipo(tipoInicial);
    setCampoSplit(widget?.campo_split ?? "");
    if (widget?.campo_grupo) {
      setCampoGrupo(widget.campo_grupo);
    } else if (tipoInicial === "bar_timeline") {
      setCampoGrupo(camposData[0]?.value ?? "");
    } else {
      setCampoGrupo(todosCamposNaoData[0]?.value ?? "");
    }
  }, [show, widget, camposData, todosCamposNaoData]);

  const handleSalvar = () => {
    if (!titulo.trim()) return;
    const splitFinal = (tipo === "kpi" || tipo === "bar_timeline")
      ? null
      : tipo === "bar_stacked"
        ? (campoSplit || todosCamposNaoData[0]?.value)
        : (campoSplit || null);
    onSalvar({
      i:              widget?.i ?? gerarId(),
      titulo:         titulo.trim(),
      tipo,
      campo_grupo:    tipo === "kpi" ? null : campoGrupo,
      campo_split:    splitFinal,
      widget_filtros: widget?.widget_filtros ?? {},
    });
  };

  const bg    = darkMode ? "#1c2235" : "#fff";
  const borda = darkMode ? "#30364a" : "#dee2e6";
  const texto = darkMode ? "#e2e8f0" : "#212529";

  const camposParaTimeline = camposData;
  const showSplit          = tipo === "bar_vertical" || tipo === "bar_horizontal" || tipo === "bar_stacked";
  const splitRequired      = tipo === "bar_stacked";
  const splitOpcoes        = splitRequired
    ? todosCamposNaoData
    : [{ value: "", label: "Nenhum" }, ...todosCamposNaoData];

  return (
    <>
      <Modal show={show} onHide={onClose} centered backdrop="static" size="md" enforceFocus={!previewTipo}>
        <Modal.Header closeButton style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda }}>
          <Modal.Title style={{ color: texto, fontSize: "1rem" }}>
            {widget ? "Editar Widget" : "Adicionar Widget"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: bg, color: texto }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: "0.83rem", fontWeight: 600 }}>Título *</Form.Label>
              <Form.Control
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex: Documentos por Situação"
                style={{ background: darkMode ? "#161b27" : undefined, color: texto, borderColor: borda }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: "0.83rem", fontWeight: 600 }}>Tipo de Gráfico</Form.Label>
              <Row>
                {TIPOS_WIDGET.map((t) => (
                  <Col xs={6} key={t.value} className="mb-2">
                    <div
                      onClick={() => {
                        setTipo(t.value);
                        if (t.value === "bar_timeline") {
                          if (camposParaTimeline.length > 0) setCampoGrupo(camposParaTimeline[0].value);
                        } else if (t.value !== "kpi") {
                          if (todosCamposNaoData.length > 0) setCampoGrupo(todosCamposNaoData[0].value);
                        }
                      }}
                      style={{
                        position: "relative",
                        padding: "10px 12px",
                        borderRadius: 8,
                        cursor: "pointer",
                        transition: "all 0.15s",
                        border: `2px solid ${tipo === t.value ? "#0d6efd" : borda}`,
                        background: tipo === t.value
                          ? (darkMode ? "#1a2d50" : "#e7f1ff")
                          : (darkMode ? "#161b27" : "#f8f9fa"),
                      }}
                    >
                      {/* Botão de preview — type="button" evita submit acidental do Form */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setPreviewTipo(t.value); }}
                        title="Ver exemplo"
                        style={{
                          position: "absolute", top: 5, right: 5,
                          background: "none", border: "none", cursor: "pointer",
                          padding: "2px 3px", lineHeight: 1,
                          opacity: 0.6, borderRadius: 3,
                          transition: "opacity 0.15s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.6; }}
                      >
                        <IconePizza size={13} />
                      </button>

                      <div style={{ fontSize: "0.82rem", fontWeight: 600, color: tipo === t.value ? "#0d6efd" : texto, paddingRight: 14 }}>
                        {t.label}
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#6c757d", marginTop: 2 }}>{t.descricao}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Form.Group>

            {tipo !== "kpi" && (
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: "0.83rem", fontWeight: 600 }}>
                  {tipo === "bar_timeline" ? "Campo de data" : "Agrupar por"}
                </Form.Label>
                {tipo === "bar_timeline" && camposParaTimeline.length === 0 ? (
                  <div style={{ fontSize: "0.8rem", color: "#dc3545", padding: "6px 0" }}>
                    Nenhum campo de data detectado nos dados. Verifique se há registros carregados.
                  </div>
                ) : (
                  <>
                    <Form.Select
                      value={campoGrupo}
                      onChange={(e) => setCampoGrupo(e.target.value)}
                      style={{ background: darkMode ? "#161b27" : undefined, color: texto, borderColor: borda }}
                    >
                      {(tipo === "bar_timeline" ? camposParaTimeline : todosCamposNaoData).map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </Form.Select>
                    {tipo === "bar_timeline" && (
                      <Form.Text style={{ color: "#6c757d", fontSize: "0.75rem" }}>
                        Agrupado por MM/AAAA — use o filtro do dashboard para definir o intervalo
                      </Form.Text>
                    )}
                  </>
                )}
              </Form.Group>
            )}

            {showSplit && (
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: "0.83rem", fontWeight: 600 }}>
                  Dividir por (segmentos){splitRequired && <span style={{ color: "#dc3545", marginLeft: 4 }}>*</span>}
                </Form.Label>
                <Form.Select
                  value={campoSplit || (splitRequired ? CAMPOS_GRAFICO[0].value : "")}
                  onChange={(e) => setCampoSplit(e.target.value)}
                  style={{ background: darkMode ? "#161b27" : undefined, color: texto, borderColor: borda }}
                >
                  {splitOpcoes.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Form.Select>
                <Form.Text style={{ color: "#6c757d", fontSize: "0.75rem" }}>
                  {splitRequired
                    ? "Cada valor deste campo vira um segmento colorido da barra"
                    : "Cria uma série por valor deste campo"}
                </Form.Text>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer style={{ background: darkMode ? "#161b27" : undefined, borderColor: borda }} className="modal-footer-padrao">
          <div className="modal-footer-acoes-esquerda" />
          <div className="modal-footer-acoes-direita">
            <Button variant="light" className="modal-footer-btn-cancelar" onClick={onClose}>Cancelar</Button>
            <Button variant="primary" className="modal-footer-btn-salvar" onClick={handleSalvar} disabled={!titulo.trim()}>
              {widget ? "Salvar" : "Adicionar"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <PreviewModal tipo={previewTipo} onClose={() => setPreviewTipo(null)} darkMode={darkMode} />
    </>
  );
}
